import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-8">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border-4 border-blue-600">
          <h1 className="text-4xl font-black text-gray-900 mb-6">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: December 2025</p>
          
          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using FltrLnk ("the Service"), you agree to be bound by these Terms of Service 
                ("Terms"). If you do not agree to these Terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Description of Service</h2>
              <p>
                FltrLnk is a next-generation user discovery platform that allows users to discover businesses, 
                opportunities, and connections through swipe-based interfaces and GPS-enabled maps. The Service 
                includes both free and premium subscription features.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Eligibility</h2>
              <p className="mb-2">
                You must be at least 18 years old to use FltrLnk. If you are under 18, you must have permission from a parent or legal guardian.
              </p>
              <p>
                Business and individual profiles must use accurate and truthful information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">4. User Accounts</h2>
              <p className="mb-2">
                To use certain features of the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activities under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Subscription and Payment</h2>
              <p>
                Premium features require a paid subscription. By subscribing, you agree to pay all applicable fees. 
                Subscriptions may be subject to automatic renewal unless cancelled. All payments are processed 
                securely through Stripe. Refunds are subject to our refund policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Early Founder Invite Rewards</h2>
              <p className="mb-4">
                FltrLnk offers an Early Founder Invite Rewards program for FltrLite Pro subscribers who refer new businesses to the platform.
              </p>
              <div className="ml-4 mt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Program Rules</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Invited businesses must be paying subscribers</strong> — Rewards are only earned when your invited businesses become paying FltrLite subscribers.</li>
                  <li><strong>Payouts are triggered after verification + retention window (e.g., 30 days)</strong> — To ensure quality referrals, payouts are processed after the referred business has been verified and has maintained their subscription for the retention period.</li>
                </ul>
              </div>
              <div className="ml-4 mt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Eligibility</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Only FltrLite Pro subscribers are eligible for invite rewards</li>
                  <li>Self-referrals are not permitted</li>
                  <li>FltrLnk reserves the right to modify or terminate the program at any time</li>
                  <li>Fraudulent or abusive referral activity will result in disqualification</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">7. User Conduct</h2>
              <p className="mb-2">You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Use the Service for any illegal purpose</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Post false, misleading, or fraudulent content</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Use automated means to access the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Impersonation & Identity Abuse</h2>
              <p className="mb-2">
                We take impersonation seriously. The following are strictly prohibited:
              </p>
              <ol className="list-decimal list-inside space-y-1 ml-4 mb-4">
                <li>Creating a profile representing another person or business without authorization.</li>
                <li>Using FltrLnk to impersonate others, even in satire or parody.</li>
              </ol>
              <p>
                Violations will result in permanent account deletion. Verified or unverified, all users are subject to identity review upon credible report.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Intellectual Property</h2>
              <p>
                All content, features, and functionality of the Service are owned by FltrLnk and are protected by 
                copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or 
                create derivative works without our express permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Termination of Use</h2>
              <p className="mb-2">
                We reserve the right to suspend or terminate any account at our discretion, particularly in the event of:
              </p>
              <ol className="list-decimal list-inside space-y-1 ml-4 mb-4">
                <li>Violate these Terms</li>
                <li>Impersonate another user or business</li>
                <li>Post harmful or illegal content</li>
                <li>Abuse monetization or engagement tools</li>
                <li>Harassment or abuse</li>
              </ol>
              <p>
                You may delete your account at any time by accessing your settings or contacting support.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Disclaimers</h2>
              <p>
                The Service is provided "as is" without warranties of any kind. We do not guarantee that the Service 
                will be uninterrupted, secure, or error-free. Use of the Service is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, FltrLnk shall not be liable for any indirect, incidental, 
                special, or consequential damages arising out of or related to your use of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Changes to Terms</h2>
              <p>
                We may modify these Terms at any time. Continued use of the Service after changes constitutes 
                acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Pregenerated Profiles & Opt-Out</h2>
              <p className="mb-4">
                To build a robust and professional network, FltrLnk may create pregenerated business or individual profiles using publicly available data or partner databases.
              </p>
              <p className="mb-4 ml-6">
                If a pregenerated profile representing you or your business is incorrect or unwanted, you can:
              </p>
              <ol className="list-decimal list-inside space-y-2 ml-12 mb-4">
                <li>Claim the profile</li>
                <li>Request its removal</li>
                <li>Transfer its ownership</li>
              </ol>
              <p className="ml-12 mb-4 font-semibold text-blue-600">
                This is your legal right under CCPA and GDPR.
              </p>
              <p className="mb-4 ml-6">
                If you discover a profile that represents you or your business and you do not want to be represented, you may:
              </p>
              <ol className="list-decimal list-inside space-y-2 ml-12 mb-4">
                <li>Email us at <a href="mailto:support@fltrlnk.com" className="text-blue-600 hover:underline">support@fltrlnk.com</a> to request removal.</li>
                <li>Include proof of identity or business representation.</li>
                <li>Upon verification, we will delete or transfer the profile within a reasonable time.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">15. Objectionable Content Policy</h2>
              <p className="mb-4">
                FltrLnk is committed to maintaining a safe, respectful, and inclusive environment for all users. 
                We prohibit content that is objectionable, harmful, or violates community standards.
              </p>
              
              <div className="ml-4 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Prohibited Content</h3>
                  <p className="mb-2">The following types of content are strictly prohibited on FltrLnk:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Illegal Content:</strong> Any content that violates local, state, federal, or international law</li>
                    <li><strong>Hate Speech:</strong> Content that promotes hatred, discrimination, or violence against individuals or groups based on race, ethnicity, religion, gender, sexual orientation, disability, or other protected characteristics</li>
                    <li><strong>Harassment & Bullying:</strong> Content intended to harass, intimidate, threaten, or bully other users</li>
                    <li><strong>Violence & Gore:</strong> Graphic violence, gore, or content that glorifies violence or harm</li>
                    <li><strong>Sexual Content:</strong> Pornography, sexually explicit material, or content that sexualizes minors</li>
                    <li><strong>Self-Harm:</strong> Content that promotes, encourages, or glorifies self-harm, suicide, or eating disorders</li>
                    <li><strong>Misinformation:</strong> False or misleading information that could cause harm, particularly related to health, safety, or civic processes</li>
                    <li><strong>Spam & Scams:</strong> Unsolicited bulk messages, fraudulent schemes, phishing attempts, or pyramid schemes</li>
                    <li><strong>Intellectual Property Violations:</strong> Content that infringes on copyrights, trademarks, or other intellectual property rights</li>
                    <li><strong>Exploitation:</strong> Content that exploits vulnerable individuals or promotes human trafficking</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Reporting Objectionable Content</h3>
                  <p className="mb-2">If you encounter content that violates this policy:</p>
                  <ol className="list-decimal list-inside space-y-1 ml-4">
                    <li>Use the in-app reporting feature to flag the content immediately</li>
                    <li>Provide detailed information about why the content is objectionable</li>
                    <li>Our moderation team will review the report within 24-48 hours</li>
                    <li>For urgent safety concerns, email <a href="mailto:support@fltrlnk.com" className="text-blue-600 hover:underline">support@fltrlnk.com</a></li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Enforcement Actions</h3>
                  <p className="mb-2">Violations of this policy may result in:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Content removal</li>
                    <li>Warning or notice to the user</li>
                    <li>Temporary suspension of account</li>
                    <li>Permanent account termination</li>
                    <li>Reporting to law enforcement (for illegal content)</li>
                    <li>Legal action where appropriate</li>
                  </ul>
                  <p className="mt-3">
                    The severity of enforcement depends on the nature and frequency of violations. 
                    Repeat offenders will face stricter penalties.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Appeals Process</h3>
                  <p>
                    If you believe your content was removed in error or your account was suspended unfairly, 
                    you may appeal by contacting <a href="mailto:support@fltrlnk.com" className="text-blue-600 hover:underline">support@fltrlnk.com</a> within 
                    30 days of the enforcement action. Include your account information and explanation for the appeal.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">User Responsibility</h3>
                  <p>
                    All users are responsible for the content they post, share, or interact with on FltrLnk. 
                    By using our platform, you acknowledge that you will not post or promote objectionable content 
                    and will respect the rights and dignity of all users.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">16. End User License Agreement (EULA)</h2>
              <p className="mb-4">
                This End User License Agreement ("EULA") is a legal agreement between you and FltrLnk Inc. 
                ("Company") for the FltrLnk mobile application and web services ("Application").
              </p>

              <div className="ml-4 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">License Grant</h3>
                  <p className="mb-2">
                    Subject to your compliance with these Terms and this EULA, FltrLnk grants you a limited, 
                    non-exclusive, non-transferable, revocable license to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Download, install, and use the Application on your personal mobile devices</li>
                    <li>Access and use the web-based services associated with the Application</li>
                    <li>Use the Application for personal or business purposes in accordance with these Terms</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">License Restrictions</h3>
                  <p className="mb-2">You may NOT:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Copy, modify, or create derivative works of the Application</li>
                    <li>Reverse engineer, decompile, or disassemble the Application</li>
                    <li>Remove, alter, or obscure any proprietary notices on the Application</li>
                    <li>Rent, lease, lend, sell, redistribute, or sublicense the Application</li>
                    <li>Use the Application for any illegal or unauthorized purpose</li>
                    <li>Attempt to gain unauthorized access to any portion of the Application</li>
                    <li>Use automated scripts, bots, or crawlers to access the Application</li>
                    <li>Interfere with or disrupt the integrity or performance of the Application</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Ownership & Intellectual Property</h3>
                  <p>
                    The Application, including all code, features, functionality, designs, logos, and content, 
                    is owned by FltrLnk Inc. and is protected by United States and international copyright, 
                    trademark, patent, trade secret, and other intellectual property laws. This EULA does not 
                    grant you any ownership rights in the Application.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Updates & Modifications</h3>
                  <p>
                    FltrLnk may provide updates, patches, or modifications to the Application at its discretion. 
                    These updates may be automatically downloaded and installed. You agree that FltrLnk may modify, 
                    suspend, or discontinue any aspect of the Application at any time without notice or liability.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Third-Party Services & Links</h3>
                  <p>
                    The Application may contain links to third-party websites, services, or content. FltrLnk does 
                    not endorse, control, or assume responsibility for any third-party content or services. Your 
                    use of third-party services is at your own risk and subject to their respective terms and policies.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Data Collection & Privacy</h3>
                  <p>
                    The Application collects and processes personal data as described in our{' '}
                    <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. 
                    By using the Application, you consent to such collection and processing. We use industry-standard 
                    security measures to protect your data, but no method of transmission is 100% secure.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Geographic Restrictions</h3>
                  <p>
                    The Application is intended for use in the United States and may not be available or appropriate 
                    for use in other locations. If you access the Application from outside the United States, you do 
                    so at your own initiative and are responsible for compliance with local laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Termination of License</h3>
                  <p className="mb-2">
                    This license is effective until terminated. Your rights under this EULA will terminate automatically if:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>You fail to comply with any terms of this EULA</li>
                    <li>You violate our Terms of Service</li>
                    <li>FltrLnk terminates your account</li>
                    <li>You uninstall or delete the Application</li>
                  </ul>
                  <p className="mt-3">
                    Upon termination, you must cease all use of the Application and delete all copies from your devices.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Warranty Disclaimer</h3>
                  <p>
                    THE APPLICATION IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS 
                    FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. FLTRLNK DOES NOT WARRANT THAT THE APPLICATION 
                    WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Limitation of Liability</h3>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL FLTRLNK BE LIABLE FOR ANY INDIRECT, 
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, 
                    OR USE, WHETHER IN AN ACTION IN CONTRACT, TORT, OR OTHERWISE, ARISING OUT OF OR IN CONNECTION 
                    WITH YOUR USE OF THE APPLICATION, EVEN IF FLTRLNK HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Indemnification</h3>
                  <p>
                    You agree to indemnify, defend, and hold harmless FltrLnk, its officers, directors, employees, 
                    and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) 
                    arising out of or related to your use of the Application, your violation of this EULA, or your 
                    infringement of any intellectual property or other rights of any third party.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Governing Law & Dispute Resolution</h3>
                  <p>
                    This EULA shall be governed by and construed in accordance with the laws of the State of California, 
                    without regard to its conflict of law principles. Any disputes arising under this EULA shall be 
                    resolved through binding arbitration in accordance with the rules of the American Arbitration 
                    Association, conducted in California.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Severability</h3>
                  <p>
                    If any provision of this EULA is found to be unenforceable or invalid, that provision shall be 
                    limited or eliminated to the minimum extent necessary so that the EULA shall otherwise remain in 
                    full force and effect.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Entire Agreement</h3>
                  <p>
                    This EULA, together with our Terms of Service and Privacy Policy, constitutes the entire agreement 
                    between you and FltrLnk regarding the Application and supersedes all prior or contemporaneous 
                    understandings and agreements.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">17. Contact Information</h2>
              <p className="mb-2">
                If you have any questions about these Terms, EULA, or our policies, please contact us:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg ml-4">
                <p><strong>General Inquiries:</strong> <a href="mailto:support@fltrlnk.com" className="text-blue-600 hover:underline">support@fltrlnk.com</a></p>
                <p><strong>Safety Concerns:</strong> <a href="mailto:support@fltrlnk.com" className="text-blue-600 hover:underline">support@fltrlnk.com</a></p>
                <p><strong>Appeals:</strong> <a href="mailto:support@fltrlnk.com" className="text-blue-600 hover:underline">support@fltrlnk.com</a></p>
                <p className="mt-2"><strong>FltrLnk Inc.</strong></p>
                <p>United States</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
